import React, { lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Layers, Minus, Plus } from 'lucide-react';
import AppHeader from '../components/ui/AppHeader';
import EacWeekPanel from '../components/executiveCalendar/EacWeekPanel';
import EacWeekSlot from '../components/executiveCalendar/EacWeekSlot';
import EacClusterPeek from '../components/executiveCalendar/EacClusterPeek';
import EventEditorPopover, {
  type EventEditorDraft,
} from '../components/executiveCalendar/EventEditorPopover';
import EventBuilderModal from '../components/executiveCalendar/EventBuilderModal';
import '../components/executiveCalendar/EventBuilderModal.css';
import { useAuth } from '../utils/AuthContext';
import { repairGoogleCalendarConnectionState } from '../utils/googleCalendarService';
import {
  DelegateCalendarEventRow,
  DelegateCalendarSummary,
  executiveCalendarDeleteEvent,
  executiveCalendarDeleteEventGroup,
  executiveCalendarInsertEvent,
  executiveCalendarListCalendars,
  executiveCalendarListEvents,
  executiveCalendarPatchEvent,
  executiveCalendarSyncEventGroup,
  getEventGroupId,
  isGroupedEvent,
} from '../utils/executiveCalendarDelegatedService';
import {
  groupSegmentsToPlanned,
  plannedToSyncPayload,
  rescheduleFromActivityChange,
  shiftScheduledSegments,
} from '../utils/calendarEventGroupPlan';
import {
  buildEventIdToGroupMap,
  findGroupByGoogleEventId,
  loadEventGroupsForExecutive,
  subscribeEventGroupsForExecutive,
} from '../utils/executiveCalendarEventGroups';
import {
  loadExecutiveCalendarSettings,
  DEFAULT_EXECUTIVE_CALENDAR_SETTINGS,
  resolvePrimaryCalendarId,
} from '../utils/executiveCalendarSettings';
import { toGoogleCalendarDateTime } from '../utils/googleCalendarDateTime';
import {
  cacheCoversWindow,
  sizeOfEventCache,
  eventsCacheKey,
  eventCacheId,
  eventsForWindow,
  mergeCacheFetch,
  removeEventsFromCache,
  pruneCacheToWindow,
  type EventsCacheBucket,
} from '../utils/executiveCalendarEventCache';
import type { DayEventCluster } from '../utils/executiveCalendarDayLayout';
import { loadIndexedEventBucket, saveIndexedEventBucket } from '../utils/executiveCalendarIndexedCache';
import { fetchMirrorEvents, subscribeMirrorEvents } from '../utils/executiveCalendarMirror';
import type { CalendarEventGroup, ExecutiveCalendarSettings } from '../types';
import {
  addMinutes,
  addWeeks,
  differenceInCalendarWeeks,
  eachDayOfInterval,
  endOfDay,
  endOfWeek,
  format,
  isSameDay,
  parseISO,
  startOfDay,
  startOfWeek,
  subWeeks,
} from 'date-fns';
import '../components/pm/pm-shared.css';
import './OperationsHub.css';
import './ExecutiveAssistantCalendarPage.css';

// Lazy so the chat (and its Firestore subscription code) costs nothing until opened.
const TravelAgentChatPanel = lazy(
  () => import('../components/executiveCalendar/TravelAgentChatPanel'),
);

const DAY_START_HOUR = 6;
const DAY_END_HOUR = 22;
const MINUTES_VISIBLE = (DAY_END_HOUR - DAY_START_HOUR) * 60;
const BASE_DAY_BODY_PX = 960;
const HOURS_SHOWN = DAY_END_HOUR - DAY_START_HOUR;
const ZOOM_MIN = 0.55;
const ZOOM_MAX = 2.4;
const ZOOM_WHEEL_SENSITIVITY = 0.002;
const NOW_TICK_MS = 5 * 60 * 1000;
const WEEKS_EACH_SIDE = 2;
const TOTAL_WEEKS = WEEKS_EACH_SIDE * 2 + 1;
const EVENTS_BUFFER_WEEKS = 1;
/** Google Calendar refetch while the tab is visible (agent + external edits). */
const EVENTS_REFRESH_VISIBLE_MS = 90 * 1000;
/** Background refetch when the tab is hidden. */
const EVENTS_REFRESH_HIDDEN_MS = 5 * 60 * 1000;
/** Debounce agent / Firestore-driven refetches so the UI does not pulse. */
const EVENTS_SIGNAL_DEBOUNCE_MS = 1200;
const PRIORITY_FETCH_WEEKS_SIDE = 1;

function mondayOf(date: Date): Date {
  return startOfWeek(date, { weekStartsOn: 1 });
}

function daysInWeek(weekMonday: Date): Date[] {
  return eachDayOfInterval({
    start: weekMonday,
    end: endOfWeek(weekMonday, { weekStartsOn: 1 }),
  });
}

function saveStoredZoom(zoom: number) {
  localStorage.setItem('ea-calendar-zoom', String(zoom));
}

function loadStoredZoom(): number {
  try {
    const raw = localStorage.getItem('ea-calendar-zoom');
    const n = raw ? parseFloat(raw) : 1;
    if (!Number.isFinite(n)) return 1;
    return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, n));
  } catch {
    return 1;
  }
}

function nowLineTopPct(now: Date): number | null {
  const mins = now.getHours() * 60 + now.getMinutes();
  const dayStart = DAY_START_HOUR * 60;
  const dayEnd = DAY_END_HOUR * 60;
  if (mins < dayStart || mins >= dayEnd) return null;
  return ((mins - dayStart) / MINUTES_VISIBLE) * 100;
}

const TEMPLATES = [
  { id: 'meeting', label: 'Meeting', durationMin: 60, title: 'Meeting' },
  { id: 'hold', label: 'Hold', durationMin: 30, title: 'Hold' },
  { id: 'travel', label: 'Travel', durationMin: 45, title: 'Travel' },
  { id: 'flight', label: 'Flight', durationMin: 180, title: 'Flight' },
] as const;

function localTz(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function canWriteCalendar(cal: DelegateCalendarSummary): boolean {
  const r = cal.accessRole;
  if (!r) return true;
  return r === 'owner' || r === 'writer';
}

function loadStoredCalendarIds(execUid: string): string[] | null {
  try {
    const raw = localStorage.getItem(`ea-calendars-${execUid}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : null;
  } catch {
    return null;
  }
}

function saveStoredCalendarIds(execUid: string, ids: string[]) {
  localStorage.setItem(`ea-calendars-${execUid}`, JSON.stringify(ids));
}

/** Cached calendar list per exec so events can start fetching without waiting on Google. */
function loadCachedCalendars(execUid: string): DelegateCalendarSummary[] | null {
  try {
    const raw = localStorage.getItem(`ea-cal-list-${execUid}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) && parsed.length ? (parsed as DelegateCalendarSummary[]) : null;
  } catch {
    return null;
  }
}

function saveCachedCalendars(execUid: string, list: DelegateCalendarSummary[]) {
  try { localStorage.setItem(`ea-cal-list-${execUid}`, JSON.stringify(list)); } catch { /* quota */ }
}

interface ExecutiveAssistantCalendarPageProps {
  /** When rendered inside the Executive Suite, the suite already owns the AppHeader/chrome. */
  embedded?: boolean;
}

const ExecutiveAssistantCalendarPage: React.FC<ExecutiveAssistantCalendarPageProps> = ({
  embedded = false,
}) => {
  const { user } = useAuth();
  const timeZone = useMemo(() => localTz(), []);
  /** Single-user for now — calendar is always the signed-in user (Matthew Lynch). */
  const execUid = user?.id ?? null;

  const [weekAnchor, setWeekAnchor] = useState(() => mondayOf(new Date()));
  const [visibleWeekIdx, setVisibleWeekIdx] = useState(WEEKS_EACH_SIDE);
  const [stripOriginMonday, setStripOriginMonday] = useState(() =>
    mondayOf(subWeeks(new Date(), WEEKS_EACH_SIDE)),
  );
  const weekStart = useMemo(() => mondayOf(weekAnchor), [weekAnchor]);
  const weekEnd = useMemo(() => endOfWeek(weekAnchor, { weekStartsOn: 1 }), [weekAnchor]);
  const weekStrip = useMemo(
    () => Array.from({ length: TOTAL_WEEKS }, (_, i) => addWeeks(stripOriginMonday, i)),
    [stripOriginMonday],
  );
  const eventsFetchWindow = useMemo(
    () => ({
      start: startOfDay(subWeeks(stripOriginMonday, EVENTS_BUFFER_WEEKS)),
      end: endOfDay(addWeeks(stripOriginMonday, TOTAL_WEEKS + EVENTS_BUFFER_WEEKS - 1)),
    }),
    [stripOriginMonday],
  );
  const priorityEventsWindow = useMemo(
    () => ({
      start: startOfDay(subWeeks(weekStart, PRIORITY_FETCH_WEEKS_SIDE)),
      end: endOfDay(addWeeks(weekEnd, PRIORITY_FETCH_WEEKS_SIDE)),
    }),
    [weekStart, weekEnd],
  );

  const pageRef = useRef<HTMLDivElement>(null);
  const hScrollRef = useRef<HTMLDivElement | null>(null);
  const vScrollRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const dayBodyPxRef = useRef(BASE_DAY_BODY_PX);
  const skipHScrollSyncRef = useRef(false);
  const skipVScrollSyncRef = useRef(false);
  const initialTodayViewDoneRef = useRef(false);
  const eventsCacheRef = useRef<EventsCacheBucket | null>(null);
  const loadEventsSeqRef = useRef(0);
  const hasLoadedEventsOnceRef = useRef(false);
  const lastGoogleSyncRef = useRef(0);
  const priorityWindowRef = useRef(priorityEventsWindow);
  priorityWindowRef.current = priorityEventsWindow;
  const fetchWindowRef = useRef(eventsFetchWindow);
  fetchWindowRef.current = eventsFetchWindow;
  const verticalScrollTopRef = useRef(0);
  const verticalScrollEndTimerRef = useRef<number | null>(null);

  const [calendarNow, setCalendarNow] = useState(() => new Date());
  const [zoom, setZoom] = useState(loadStoredZoom);

  const dayBodyPx = useMemo(() => Math.round(BASE_DAY_BODY_PX * zoom), [zoom]);
  const hourCellPx = useMemo(() => dayBodyPx / HOURS_SHOWN, [dayBodyPx]);
  dayBodyPxRef.current = dayBodyPx;

  const nowTopPct = useMemo(() => nowLineTopPct(calendarNow), [calendarNow]);

  useEffect(() => {
    saveStoredZoom(zoom);
  }, [zoom]);

  useEffect(() => {
    const tick = () => setCalendarNow(new Date());
    tick();
    const id = window.setInterval(tick, NOW_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const nudgeZoom = useCallback((direction: -1 | 1) => {
    setZoom(prev => {
      const step = prev < 1 ? 0.08 : 0.1;
      const next = direction < 0 ? prev - step : prev + step;
      return Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(next * 100) / 100));
    });
  }, []);

  const releaseWeekScrollerPositioning = useCallback((behavior: ScrollBehavior = 'auto') => {
    const el = hScrollRef.current;
    if (!el) return;
    el.classList.remove('eac-week-scroller--positioning');
    el.style.scrollBehavior = '';
    window.setTimeout(() => {
      skipHScrollSyncRef.current = false;
    }, behavior === 'smooth' ? 500 : 0);
  }, []);

  const scrollToWeekIndex = useCallback(
    (index: number, behavior: ScrollBehavior = 'smooth'): boolean => {
      const el = hScrollRef.current;
      if (!el || el.clientWidth <= 0) return false;

      skipHScrollSyncRef.current = true;
      setVisibleWeekIdx(index);

      const targetLeft = index * el.clientWidth;
      el.classList.add('eac-week-scroller--positioning');
      el.style.scrollBehavior = behavior === 'smooth' ? 'smooth' : 'auto';
      el.scrollLeft = targetLeft;

      const ok = Math.abs(el.scrollLeft - targetLeft) <= 2;
      if (ok) {
        if (behavior === 'smooth') {
          const release = () => releaseWeekScrollerPositioning(behavior);
          if ('onscrollend' in el) {
            el.addEventListener('scrollend', release, { once: true });
          }
          window.setTimeout(release, 600);
        } else {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => releaseWeekScrollerPositioning(behavior));
          });
        }
      }

      return ok;
    },
    [releaseWeekScrollerPositioning],
  );

  const navigateToMonday = useCallback(
    (target: Date, behavior: ScrollBehavior = 'smooth') => {
      const monday = mondayOf(target);
      setWeekAnchor(monday);
      const idx = differenceInCalendarWeeks(monday, stripOriginMonday, { weekStartsOn: 1 });
      if (idx < 2 || idx > TOTAL_WEEKS - 3) {
        setStripOriginMonday(subWeeks(monday, WEEKS_EACH_SIDE));
        window.requestAnimationFrame(() => scrollToWeekIndex(WEEKS_EACH_SIDE, 'auto'));
        return;
      }
      scrollToWeekIndex(idx, behavior);
    },
    [scrollToWeekIndex, stripOriginMonday],
  );

  const syncWeekFromScroll = useCallback(() => {
    if (!initialTodayViewDoneRef.current) return;
    const el = hScrollRef.current;
    if (!el || el.clientWidth <= 0) return;
    const idx = Math.max(0, Math.min(TOTAL_WEEKS - 1, Math.round(el.scrollLeft / el.clientWidth)));
    setVisibleWeekIdx(prev => (prev === idx ? prev : idx));
    const monday = weekStrip[idx];
    if (monday && !isSameDay(monday, weekStart)) {
      setWeekAnchor(monday);
    }
  }, [weekStart, weekStrip]);

  const flushVerticalScrollSync = useCallback((sourceIdx: number) => {
    const scrollTop = verticalScrollTopRef.current;
    skipVScrollSyncRef.current = true;
    vScrollRefs.current.forEach((node, idx) => {
      if (idx !== sourceIdx && Math.abs(idx - visibleWeekIdx) <= 1) {
        if (Math.abs(node.scrollTop - scrollTop) > 1) {
          node.scrollTop = scrollTop;
        }
      }
    });
    window.requestAnimationFrame(() => {
      skipVScrollSyncRef.current = false;
    });
  }, [visibleWeekIdx]);

  const handleVerticalScroll = useCallback(
    (sourceIdx: number, scrollTop: number) => {
      if (skipVScrollSyncRef.current) return;
      verticalScrollTopRef.current = scrollTop;
      if (verticalScrollEndTimerRef.current != null) {
        window.clearTimeout(verticalScrollEndTimerRef.current);
      }
      verticalScrollEndTimerRef.current = window.setTimeout(() => {
        verticalScrollEndTimerRef.current = null;
        flushVerticalScrollSync(sourceIdx);
      }, 100);
    },
    [flushVerticalScrollSync],
  );

  const registerVScrollEl = useCallback((weekIdx: number, node: HTMLDivElement | null) => {
    if (node) vScrollRefs.current.set(weekIdx, node);
    else vScrollRefs.current.delete(weekIdx);
  }, []);

  useEffect(() => {
    const node = vScrollRefs.current.get(visibleWeekIdx);
    if (!node) return;
    const target = verticalScrollTopRef.current;
    if (Math.abs(node.scrollTop - target) < 1) return;
    skipVScrollSyncRef.current = true;
    node.scrollTop = target;
    window.requestAnimationFrame(() => {
      skipVScrollSyncRef.current = false;
    });
    flushVerticalScrollSync(visibleWeekIdx);
  }, [visibleWeekIdx, flushVerticalScrollSync]);

  useEffect(
    () => () => {
      if (verticalScrollEndTimerRef.current != null) {
        window.clearTimeout(verticalScrollEndTimerRef.current);
      }
    },
    [],
  );

  const scrollVerticalToNow = useCallback(
    (weekIdx: number, behavior: ScrollBehavior = 'auto') => {
      const pct = nowLineTopPct(new Date());
      if (pct === null) return;
      const scrollEl = vScrollRefs.current.get(weekIdx);
      if (!scrollEl) return;
      const top = (pct / 100) * dayBodyPxRef.current - scrollEl.clientHeight * 0.35;
      const scrollTop = Math.max(0, top);
      skipVScrollSyncRef.current = true;
      verticalScrollTopRef.current = scrollTop;
      vScrollRefs.current.forEach(node => {
        node.scrollTo({ top: scrollTop, behavior });
      });
      window.requestAnimationFrame(() => {
        skipVScrollSyncRef.current = false;
      });
    },
    [],
  );

  const goToTodayView = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      initialTodayViewDoneRef.current = false;
      const todayMonday = mondayOf(new Date());
      setWeekAnchor(todayMonday);

      let idx = differenceInCalendarWeeks(todayMonday, stripOriginMonday, { weekStartsOn: 1 });
      if (idx < 2 || idx > TOTAL_WEEKS - 3) {
        setStripOriginMonday(subWeeks(todayMonday, WEEKS_EACH_SIDE));
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            scrollToWeekIndex(WEEKS_EACH_SIDE, behavior);
            scrollVerticalToNow(WEEKS_EACH_SIDE, behavior);
            initialTodayViewDoneRef.current = true;
          });
        });
        return;
      }

      scrollToWeekIndex(idx, behavior);
      window.requestAnimationFrame(() => {
        scrollVerticalToNow(idx, behavior);
        initialTodayViewDoneRef.current = true;
      });
    },
    [scrollToWeekIndex, scrollVerticalToNow, stripOriginMonday],
  );

  const [calendars, setCalendars] = useState<DelegateCalendarSummary[]>([]);
  const [selectedCalIds, setSelectedCalIds] = useState<string[]>([]);
  const [events, setEvents] = useState<DelegateCalendarEventRow[]>([]);

  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showCalendarGrid = Boolean(execUid);
  const showInitialLoader = !execUid;
  const calendarGridReady = showCalendarGrid;

  useLayoutEffect(() => {
    initialTodayViewDoneRef.current = false;
  }, [execUid]);

  useLayoutEffect(() => {
    if (!calendarGridReady || initialTodayViewDoneRef.current) return;

    let cancelled = false;
    let attempts = 0;

    const positionToday = () => {
      if (cancelled || initialTodayViewDoneRef.current) return;

      attempts += 1;
      if (attempts > 100) {
        releaseWeekScrollerPositioning('auto');
        initialTodayViewDoneRef.current = true;
        return;
      }

      const el = hScrollRef.current;
      if (!el || el.clientWidth <= 0) {
        window.requestAnimationFrame(positionToday);
        return;
      }

      const todayMonday = mondayOf(new Date());
      const idx = differenceInCalendarWeeks(todayMonday, stripOriginMonday, { weekStartsOn: 1 });
      if (idx < 0 || idx >= TOTAL_WEEKS) {
        setStripOriginMonday(subWeeks(todayMonday, WEEKS_EACH_SIDE));
        setWeekAnchor(todayMonday);
        return;
      }

      setWeekAnchor(todayMonday);
      skipHScrollSyncRef.current = true;
      setVisibleWeekIdx(idx);

      const targetLeft = idx * el.clientWidth;
      el.classList.add('eac-week-scroller--positioning');
      el.style.scrollBehavior = 'auto';
      el.scrollLeft = targetLeft;

      if (Math.abs(el.scrollLeft - targetLeft) > 2) {
        window.requestAnimationFrame(positionToday);
        return;
      }

      const scrollEl = vScrollRefs.current.get(idx);
      if (!scrollEl || scrollEl.clientHeight <= 0) {
        window.requestAnimationFrame(positionToday);
        return;
      }

      scrollVerticalToNow(idx, 'auto');

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (cancelled) return;
          releaseWeekScrollerPositioning('auto');
          initialTodayViewDoneRef.current = true;
        });
      });
    };

    positionToday();

    const el = hScrollRef.current;
    const ro =
      el &&
      new ResizeObserver(() => {
        if (!initialTodayViewDoneRef.current) positionToday();
      });
    if (el && ro) ro.observe(el);

    return () => {
      cancelled = true;
      ro?.disconnect();
    };
  }, [
    calendarGridReady,
    execUid,
    stripOriginMonday,
    scrollVerticalToNow,
    releaseWeekScrollerPositioning,
    dayBodyPx,
    zoom,
  ]);

  const bindHScrollRef = useCallback((node: HTMLDivElement | null) => {
    hScrollRef.current = node;
  }, []);

  useEffect(() => {
    if (!calendarGridReady) return;
    const el = hScrollRef.current;
    if (!el) return;

    let scrollEndTimer: ReturnType<typeof setTimeout> | undefined;
    const onScrollEnd = () => {
      if (skipHScrollSyncRef.current) return;
      syncWeekFromScroll();
    };
    const onScroll = () => {
      if (scrollEndTimer) clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(onScrollEnd, 120);
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    if ('onscrollend' in el) {
      el.addEventListener('scrollend', onScrollEnd);
    }

    return () => {
      el.removeEventListener('scroll', onScroll);
      if ('onscrollend' in el) {
        el.removeEventListener('scrollend', onScrollEnd);
      }
      if (scrollEndTimer) clearTimeout(scrollEndTimer);
    };
  }, [syncWeekFromScroll, calendarGridReady]);

  useEffect(() => {
    document.body.classList.add('eac-route-active');
    return () => document.body.classList.remove('eac-route-active');
  }, []);

  useEffect(() => {
    const el = pageRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      e.stopPropagation();
      const factor = 1 - e.deltaY * ZOOM_WHEEL_SENSITIVITY;
      setZoom(prev => {
        const next = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, prev * factor));
        return Math.round(next * 100) / 100;
      });
    };

    el.addEventListener('wheel', onWheel, { passive: false, capture: true });
    return () => el.removeEventListener('wheel', onWheel, { capture: true });
  }, []);

  const [composerOpen, setComposerOpen] = useState(false);
  const [composerDay, setComposerDay] = useState<Date | null>(null);
  const [composerStartMin, setComposerStartMin] = useState(9 * 60);
  const [composerTitle, setComposerTitle] = useState('');
  const [composerDuration, setComposerDuration] = useState(60);
  const [composerCalId, setComposerCalId] = useState('');
  const [composerLocation, setComposerLocation] = useState('');
  const [composerDesc, setComposerDesc] = useState('');

  const [editState, setEditState] = useState<{
    ev: DelegateCalendarEventRow;
    anchor: DOMRect;
  } | null>(null);
  const editStateRef = useRef(editState);
  editStateRef.current = editState;

  const [peek, setPeek] = useState<{ cluster: DayEventCluster; anchor: DOMRect } | null>(null);
  const peekOpenTimerRef = useRef<number | null>(null);
  const peekCloseTimerRef = useRef<number | null>(null);
  const [travelChatOpen, setTravelChatOpen] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderDay, setBuilderDay] = useState<Date | null>(null);
  const [builderStartMin, setBuilderStartMin] = useState(9 * 60);
  const [slotMenu, setSlotMenu] = useState<{ day: Date; minute: number; x: number; y: number } | null>(null);
  const [calSettings, setCalSettings] = useState<ExecutiveCalendarSettings>(
    DEFAULT_EXECUTIVE_CALENDAR_SETTINGS,
  );
  const [eventGroups, setEventGroups] = useState<CalendarEventGroup[]>([]);

  const dragRef = useRef<{
    ev: DelegateCalendarEventRow;
    pointerId: number;
    startMs: number;
    endMs: number;
    originY: number;
    moved: boolean;
  } | null>(null);

  const writableCalendars = useMemo(
    () => calendars.filter(c => canWriteCalendar(c)),
    [calendars],
  );
  const writableCalendarIds = useMemo(
    () => writableCalendars.map(c => c.id),
    [writableCalendars],
  );
  const eventIdToGroup = useMemo(() => buildEventIdToGroupMap(eventGroups), [eventGroups]);
  const calendarColors = useMemo(() => {
    const map = new Map<string, string>();
    for (let i = 0; i < calendars.length; i++) {
      const cal = calendars[i];
      map.set(cal.id, cal.backgroundColor || '#bfdbfe');
    }
    return map;
  }, [calendars]);

  const loadGroups = useCallback(async () => {
    if (!execUid) {
      setEventGroups([]);
      return;
    }
    try {
      const groups = await loadEventGroupsForExecutive(execUid);
      setEventGroups(groups);
    } catch {
      setEventGroups([]);
    }
  }, [execUid]);

  const loadSettings = useCallback(async () => {
    if (!execUid) {
      setCalSettings(DEFAULT_EXECUTIVE_CALENDAR_SETTINGS);
      return;
    }
    try {
      const s = await loadExecutiveCalendarSettings(execUid);
      setCalSettings(s);
    } catch {
      setCalSettings(DEFAULT_EXECUTIVE_CALENDAR_SETTINGS);
    }
  }, [execUid]);

  useEffect(() => {
    if (!execUid) {
      setCalendars([]);
      setSelectedCalIds([]);
      setEvents([]);
      return;
    }

    let cancelled = false;

    const applyCalendarList = (list: DelegateCalendarSummary[], persist: boolean) => {
      setCalendars(list);
      const writableIds = list.filter(c => canWriteCalendar(c)).map(c => c.id);
      const stored = loadStoredCalendarIds(execUid);
      let nextSel = stored?.filter(id => list.some(c => c.id === id)) ?? [];

      if (nextSel.length === 0) {
        const primaryId = resolvePrimaryCalendarId(list, writableIds);
        nextSel = primaryId ? [primaryId] : list.length > 0 ? [list[0].id] : [];
      }
      if (nextSel.length === 0 && list.length > 0) {
        nextSel = [list[0].id];
      }

      setSelectedCalIds((prev) => (
        prev.length === nextSel.length && prev.every((id, i) => id === nextSel[i]) ? prev : nextSel
      ));
      if (persist && nextSel.length) saveStoredCalendarIds(execUid, nextSel);

      const primaryWritable = writableIds[0];
      setComposerCalId(prev => {
        if (prev && writableIds.includes(prev)) return prev;
        return primaryWritable || nextSel[0] || '';
      });
    };

    const loadCals = async () => {
      // Warm start: use the cached calendar list so events begin fetching
      // immediately; the fresh list reconciles in the background.
      const cached = loadCachedCalendars(execUid);
      setEvents([]);
      if (cached) {
        applyCalendarList(cached, false);
      } else {
        setSelectedCalIds([]);
        setLoadingCalendars(true);
      }
      setError(null);

      // Connection repair is a self-healing housekeeping step — never gate the
      // calendar list on it.
      void repairGoogleCalendarConnectionState(execUid).catch(() => false);

      try {
        const { calendars: list } = await executiveCalendarListCalendars(execUid);
        if (cancelled) return;
        saveCachedCalendars(execUid, list);
        applyCalendarList(list, true);
      } catch (e) {
        if (!cancelled && !cached) {
          setError(e instanceof Error ? e.message : 'Failed to load calendars');
          setCalendars([]);
          setSelectedCalIds([]);
        }
      } finally {
        if (!cancelled) setLoadingCalendars(false);
      }
    };

    void loadCals();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execUid]);

  const loadEvents = useCallback(
    async (options?: { background?: boolean; force?: boolean; fullWindow?: boolean }) => {
      if (!execUid || selectedCalIds.length === 0) {
        eventsCacheRef.current = null;
        setEvents([]);
        return;
      }

      const cacheKey = eventsCacheKey(execUid, selectedCalIds);
      const displayStartMs = priorityWindowRef.current.start.getTime();
      const displayEndMs = priorityWindowRef.current.end.getTime();
      const fullStartMs = fetchWindowRef.current.start.getTime();
      const fullEndMs = fetchWindowRef.current.end.getTime();

      // Layer 1: IndexedDB — paint before any network.
      if (!eventsCacheRef.current) {
        const indexed = await loadIndexedEventBucket(cacheKey);
        if (indexed?.cacheKey === cacheKey) {
          eventsCacheRef.current = indexed;
        }
      }

      let bucket = eventsCacheRef.current;
      const hasFullCoverage = cacheCoversWindow(bucket, cacheKey, fullStartMs, fullEndMs);
      const hasDisplayCoverage = cacheCoversWindow(bucket, cacheKey, displayStartMs, displayEndMs);
      const stale =
        lastGoogleSyncRef.current === 0 ||
        Date.now() - lastGoogleSyncRef.current > EVENTS_REFRESH_VISIBLE_MS;

      const applyDisplayWindow = (source: EventsCacheBucket | null) => {
        if (!source) {
          setEvents([]);
          return;
        }
        const useFull = cacheCoversWindow(source, cacheKey, fullStartMs, fullEndMs);
        setEvents(
          eventsForWindow(
            source,
            useFull ? fullStartMs : displayStartMs,
            useFull ? fullEndMs : displayEndMs,
          ),
        );
      };

      const persistBucket = (next: EventsCacheBucket) => {
        eventsCacheRef.current = next;
        void saveIndexedEventBucket(next);
      };

      if (hasDisplayCoverage && bucket) {
        applyDisplayWindow(bucket);
        if (!options?.force && !stale && hasFullCoverage) return;
      } else if (bucket?.cacheKey === cacheKey && !options?.background) {
        applyDisplayWindow(bucket);
      }

      if (hasFullCoverage && !stale && !options?.force) return;

      const cachedCount = bucket != null ? sizeOfEventCache(bucket) : 0;
      const showBlockingLoader =
        !options?.background &&
        !hasLoadedEventsOnceRef.current &&
        !hasDisplayCoverage &&
        cachedCount === 0;
      if (showBlockingLoader) setLoadingData(true);
      setError(null);

      const seq = ++loadEventsSeqRef.current;

      const mergeFetched = (
        fetched: DelegateCalendarEventRow[],
        windowStartMs: number,
        windowEndMs: number,
      ) =>
        pruneCacheToWindow(
          mergeCacheFetch(
            eventsCacheRef.current,
            cacheKey,
            fetched,
            windowStartMs,
            windowEndMs,
          ),
          fullStartMs,
          fullEndMs,
        );

      const fetchAndMergeMirror = async (windowStartMs: number, windowEndMs: number) => {
        const fetched = await fetchMirrorEvents(
          execUid,
          selectedCalIds,
          windowStartMs,
          windowEndMs,
        );
        if (seq !== loadEventsSeqRef.current) return null;
        if (fetched.length === 0) return null;
        return mergeFetched(fetched, windowStartMs, windowEndMs);
      };

      const fetchAndMergeGoogle = async (windowStartMs: number, windowEndMs: number) => {
        const { events: fetched } = await executiveCalendarListEvents({
          executiveUid: execUid,
          calendarIds: selectedCalIds,
          timeMin: new Date(windowStartMs).toISOString(),
          timeMax: new Date(windowEndMs).toISOString(),
        });
        if (seq !== loadEventsSeqRef.current) return null;
        lastGoogleSyncRef.current = Date.now();
        return mergeFetched(fetched, windowStartMs, windowEndMs);
      };

      const applyFullWindowFetch = async (source: 'mirror' | 'google') => {
        const merged =
          source === 'mirror'
            ? await fetchAndMergeMirror(fullStartMs, fullEndMs)
            : await fetchAndMergeGoogle(fullStartMs, fullEndMs);
        if (merged && seq === loadEventsSeqRef.current) {
          persistBucket(merged);
          applyDisplayWindow(merged);
          hasLoadedEventsOnceRef.current = true;
        }
      };

      try {
        // Layer 2: Firestore mirror (fast read, no Google quota).
        let didPriorityMirror = false;
        if (!hasDisplayCoverage || options?.force) {
          const mirrorQuick = await fetchAndMergeMirror(displayStartMs, displayEndMs);
          if (mirrorQuick) {
            persistBucket(mirrorQuick);
            applyDisplayWindow(mirrorQuick);
            hasLoadedEventsOnceRef.current = true;
            didPriorityMirror = true;
          }
        }

        bucket = eventsCacheRef.current;
        const mirrorCoversFull = cacheCoversWindow(bucket, cacheKey, fullStartMs, fullEndMs);

        if (!mirrorCoversFull || options?.force) {
          if (didPriorityMirror && !options?.force) {
            void applyFullWindowFetch('mirror');
          } else {
            await applyFullWindowFetch('mirror');
          }
        }

        bucket = eventsCacheRef.current;
        const afterMirrorFull = cacheCoversWindow(bucket, cacheKey, fullStartMs, fullEndMs);
        const needsGoogle = !afterMirrorFull || stale || options?.force;

        if (!needsGoogle) return;

        // Layer 3: Google Calendar (authoritative sync + mirror write on server).
        let didPriorityGoogle = false;
        if (!hasDisplayCoverage || options?.force) {
          const quick = await fetchAndMergeGoogle(displayStartMs, displayEndMs);
          if (quick) {
            persistBucket(quick);
            applyDisplayWindow(quick);
            hasLoadedEventsOnceRef.current = true;
            didPriorityGoogle = true;
          }
        }

        bucket = eventsCacheRef.current;
        const googleCoversFull = cacheCoversWindow(bucket, cacheKey, fullStartMs, fullEndMs);

        if (!googleCoversFull || options?.force) {
          if (didPriorityGoogle && !options?.force) {
            void applyFullWindowFetch('google');
          } else {
            await applyFullWindowFetch('google');
          }
        }
      } catch (e) {
        if (seq !== loadEventsSeqRef.current) return;
        setError(e instanceof Error ? e.message : 'Failed to load events');
        if (!hasDisplayCoverage) {
          eventsCacheRef.current = null;
          setEvents([]);
        }
      } finally {
        if (seq === loadEventsSeqRef.current) {
          setLoadingData(false);
        }
      }
    },
    [execUid, selectedCalIds],
  );

  const loadEventsRef = useRef(loadEvents);
  loadEventsRef.current = loadEvents;

  useEffect(() => {
    eventsCacheRef.current = null;
    hasLoadedEventsOnceRef.current = false;
    lastGoogleSyncRef.current = 0;
  }, [execUid, selectedCalIds]);

  const [tabVisible, setTabVisible] = useState(
    () => typeof document !== 'undefined' && document.visibilityState === 'visible',
  );

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  // Live mirror updates (another tab or background Google sync).
  useEffect(() => {
    if (!execUid || selectedCalIds.length === 0) return;
    const cacheKey = eventsCacheKey(execUid, selectedCalIds);
    const fullStartMs = fetchWindowRef.current.start.getTime();
    const fullEndMs = fetchWindowRef.current.end.getTime();
    const displayStartMs = priorityWindowRef.current.start.getTime();
    const displayEndMs = priorityWindowRef.current.end.getTime();

    const unsub = subscribeMirrorEvents(
      execUid,
      selectedCalIds,
      fullStartMs,
      fullEndMs,
      (mirrorEvents) => {
        const merged = pruneCacheToWindow(
          mergeCacheFetch(
            eventsCacheRef.current,
            cacheKey,
            mirrorEvents,
            fullStartMs,
            fullEndMs,
          ),
          fullStartMs,
          fullEndMs,
        );
        eventsCacheRef.current = merged;
        void saveIndexedEventBucket(merged);
        const useFull = cacheCoversWindow(merged, cacheKey, fullStartMs, fullEndMs);
        setEvents(
          eventsForWindow(
            merged,
            useFull ? fullStartMs : displayStartMs,
            useFull ? fullEndMs : displayEndMs,
          ),
        );
        hasLoadedEventsOnceRef.current = true;
      },
    );
    return unsub;
  }, [execUid, selectedCalIds, eventsFetchWindow, priorityEventsWindow]);

  // Quiet background sync — only when cache is stale, no loading bar.
  useEffect(() => {
    const ms = tabVisible ? EVENTS_REFRESH_VISIBLE_MS : EVENTS_REFRESH_HIDDEN_MS;
    const id = window.setInterval(() => {
      void loadEventsRef.current({ background: true });
    }, ms);
    return () => window.clearInterval(id);
  }, [tabVisible]);

  useEffect(() => {
    const onVis = () => setTabVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  // Refetch after returning to the tab (debounced).
  useEffect(() => {
    let timer: number | null = null;
    const refresh = () => {
      if (timer != null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        void loadEventsRef.current({ background: true, force: true });
      }, EVENTS_SIGNAL_DEBOUNCE_MS);
    };
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      if (timer != null) window.clearTimeout(timer);
    };
  }, []);

  // Real-time: Firestore event-group changes debounced — no loading bar.
  useEffect(() => {
    if (!execUid) {
      setEventGroups([]);
      return;
    }
    let first = true;
    let debounceTimer: number | null = null;
    const unsub = subscribeEventGroupsForExecutive(execUid, groups => {
      setEventGroups(groups);
      if (!first) {
        if (debounceTimer != null) window.clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(() => {
          void loadEventsRef.current({ force: true, background: true });
        }, EVENTS_SIGNAL_DEBOUNCE_MS);
      }
      first = false;
    });
    return () => {
      unsub();
      if (debounceTimer != null) window.clearTimeout(debounceTimer);
    };
  }, [execUid]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  const reloadAll = useCallback(async () => {
    await Promise.all([loadEvents({ force: true }), loadGroups()]);
  }, [loadEvents, loadGroups]);

  const toggleCal = (id: string, checked: boolean) => {
    setSelectedCalIds(prev => {
      const next = checked ? [...prev, id] : prev.filter(x => x !== id);
      if (execUid) saveStoredCalendarIds(execUid, next);
      return next;
    });
  };

  const eventsByDate = useMemo(() => {
    const map = new Map<string, DelegateCalendarEventRow[]>();

    for (const ev of events) {
      if (!ev.start || ev.isAllDay) continue;
      let start: Date;
      try {
        start = parseISO(ev.start);
      } catch {
        continue;
      }
      const key = format(startOfDay(start), 'yyyy-MM-dd');
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(ev);
    }

    map.forEach(arr => {
      arr.sort((a, b) => {
        const ta = a.start ? parseISO(a.start).getTime() : 0;
        const tb = b.start ? parseISO(b.start).getTime() : 0;
        return ta - tb;
      });
    });
    return map;
  }, [events]);

  const openComposerAt = (day: Date, minuteOfDay: number) => {
    setSlotMenu(null);
    setComposerDay(day);
    setComposerStartMin(minuteOfDay);
    setComposerTitle('');
    setComposerDuration(60);
    setComposerLocation('');
    setComposerDesc('');
    if (writableCalendars[0]) setComposerCalId(writableCalendars[0].id);
    setComposerOpen(true);
  };

  const openBuilderAt = (day: Date, minuteOfDay: number) => {
    setSlotMenu(null);
    setBuilderDay(day);
    setBuilderStartMin(minuteOfDay);
    setBuilderOpen(true);
  };

  const openSlotMenu = (e: React.MouseEvent, day: Date, minuteOfDay: number) => {
    e.stopPropagation();
    setSlotMenu({ day, minute: minuteOfDay, x: e.clientX, y: e.clientY });
  };

  const handleDayBodyClick = useCallback((e: React.MouseEvent<HTMLDivElement>, day: Date) => {
    if ((e.target as HTMLElement).closest('.eac-event-pill')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.height <= 0) return;
    const pct = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    const rawMin = DAY_START_HOUR * 60 + pct * MINUTES_VISIBLE;
    const snapped = Math.round(rawMin / 15) * 15;
    const minuteOfDay = Math.min(
      DAY_END_HOUR * 60 - 15,
      Math.max(DAY_START_HOUR * 60, snapped),
    );
    openSlotMenu(e, day, minuteOfDay);
  }, []);

  const applyTemplate = (tid: (typeof TEMPLATES)[number]['id']) => {
    const t = TEMPLATES.find(x => x.id === tid);
    if (!t) return;
    setComposerTitle(t.title);
    setComposerDuration(t.durationMin);
  };

  const submitComposer = async () => {
    if (!execUid || !composerDay || !composerCalId || !composerTitle.trim()) return;
    const start = new Date(composerDay);
    const h = Math.floor(composerStartMin / 60);
    const m = composerStartMin % 60;
    start.setHours(h, m, 0, 0);
    const end = addMinutes(start, composerDuration);
    try {
      await executiveCalendarInsertEvent({
        executiveUid: execUid,
        calendarId: composerCalId,
        body: {
          summary: composerTitle.trim(),
          description: composerDesc.trim() || undefined,
          location: composerLocation.trim() || undefined,
          start: { dateTime: toGoogleCalendarDateTime(start), timeZone },
          end: { dateTime: toGoogleCalendarDateTime(end), timeZone },
        },
      });
      setComposerOpen(false);
      await reloadAll();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Could not create event');
    }
  };

  const clearPeekTimers = useCallback(() => {
    if (peekOpenTimerRef.current != null) {
      window.clearTimeout(peekOpenTimerRef.current);
      peekOpenTimerRef.current = null;
    }
    if (peekCloseTimerRef.current != null) {
      window.clearTimeout(peekCloseTimerRef.current);
      peekCloseTimerRef.current = null;
    }
  }, []);

  const closePeek = useCallback(() => {
    clearPeekTimers();
    setPeek(null);
  }, [clearPeekTimers]);

  const handleEventHover = useCallback(
    (
      ev: DelegateCalendarEventRow,
      cluster: DayEventCluster,
      anchorRect: DOMRect,
      isShort: boolean,
    ) => {
      if (editStateRef.current) return;
      if (peekCloseTimerRef.current != null) {
        window.clearTimeout(peekCloseTimerRef.current);
        peekCloseTimerRef.current = null;
      }
      if (peekOpenTimerRef.current != null) {
        window.clearTimeout(peekOpenTimerRef.current);
        peekOpenTimerRef.current = null;
      }
      // Only crowded clusters or too-short-to-read pills warrant the peek.
      if (cluster.events.length < 2 && !isShort) return;
      peekOpenTimerRef.current = window.setTimeout(() => {
        peekOpenTimerRef.current = null;
        setPeek({ cluster, anchor: anchorRect });
      }, 250);
    },
    [],
  );

  const handleEventHoverEnd = useCallback(() => {
    if (peekOpenTimerRef.current != null) {
      window.clearTimeout(peekOpenTimerRef.current);
      peekOpenTimerRef.current = null;
    }
    if (peekCloseTimerRef.current != null) window.clearTimeout(peekCloseTimerRef.current);
    // Grace period so the pointer can travel from the pill into the card.
    peekCloseTimerRef.current = window.setTimeout(() => {
      peekCloseTimerRef.current = null;
      setPeek(null);
    }, 200);
  }, []);

  const cancelPeekClose = useCallback(() => {
    if (peekCloseTimerRef.current != null) {
      window.clearTimeout(peekCloseTimerRef.current);
      peekCloseTimerRef.current = null;
    }
  }, []);

  const openEdit = useCallback(
    (ev: DelegateCalendarEventRow, anchorRect: DOMRect) => {
      if (!ev.start || !ev.end) return;
      closePeek();
      setEditState({ ev, anchor: anchorRect });
    },
    [closePeek],
  );

  const saveEdit = async (draft: EventEditorDraft) => {
    const current = editStateRef.current;
    if (!execUid || !current) return;
    const editEvent = current.ev;
    const { start, end } = draft;
    const durationMin = Math.max(1, Math.round((end.getTime() - start.getTime()) / 60000));

    const group = findGroupByGoogleEventId(eventGroups, editEvent.id);
    if (group?.id) {
      const segIdx = group.segments.findIndex(s => s.googleEventId === editEvent.id);
      const seg = group.segments[segIdx];
      let planned = groupSegmentsToPlanned(group);

      if (seg?.type === 'activity' && segIdx === group.anchorSegmentIndex) {
        planned[segIdx].title = draft.title;
        planned = rescheduleFromActivityChange(
          planned,
          group.anchorMode,
          group.anchorSegmentIndex,
          start,
          durationMin,
        );
      } else if (editEvent.start) {
        const oldStart = parseISO(editEvent.start);
        const deltaMs = start.getTime() - oldStart.getTime();
        if (seg?.type === 'activity') {
          planned[segIdx].title = draft.title;
          planned[segIdx].durationMinutes = durationMin;
          let cursor = start;
          for (let i = segIdx; i < planned.length; i++) {
            planned[i].start = cursor;
            planned[i].end = addMinutes(cursor, planned[i].durationMinutes);
            cursor = planned[i].end!;
          }
        } else {
          planned = shiftScheduledSegments(planned, deltaMs);
        }
      }

      await executiveCalendarSyncEventGroup({
        executiveUid: execUid,
        groupId: group.id,
        timeZone,
        segments: plannedToSyncPayload(planned),
      });
    } else {
      await executiveCalendarPatchEvent({
        executiveUid: execUid,
        calendarId: editEvent.calendarId,
        eventId: editEvent.id,
        resource: {
          summary: draft.title,
          location: draft.location,
          description: draft.description,
          start: { dateTime: toGoogleCalendarDateTime(start), timeZone },
          end: { dateTime: toGoogleCalendarDateTime(end), timeZone },
        },
      });
    }

    setEditState(null);
    await reloadAll();
  };

  const deleteEdit = async () => {
    const current = editStateRef.current;
    if (!execUid || !current) return;
    const editEvent = current.ev;
    const group = findGroupByGoogleEventId(eventGroups, editEvent.id);
    const linkedGroupId = getEventGroupId(editEvent);
    const msg = group?.id || linkedGroupId
      ? 'Delete this entire trip (all linked travel and activity events)?'
      : 'Delete this event from Google Calendar?';
    if (!window.confirm(msg)) return;
    if (group?.id) {
      await executiveCalendarDeleteEventGroup({ executiveUid: execUid, groupId: group.id });
    } else if (linkedGroupId) {
      const related = events.filter(ev => getEventGroupId(ev) === linkedGroupId);
      for (const rel of related) {
        await executiveCalendarDeleteEvent({
          executiveUid: execUid,
          calendarId: rel.calendarId,
          eventId: rel.id,
        });
      }
    } else {
      await executiveCalendarDeleteEvent({
        executiveUid: execUid,
        calendarId: editEvent.calendarId,
        eventId: editEvent.id,
      });
    }

    if (selectedCalIds.length > 0) {
      const cacheKey = eventsCacheKey(execUid, selectedCalIds);
      const keysToDrop =
        group?.id
          ? group.segments
              .filter(seg => seg.googleEventId && seg.calendarId)
              .map(seg => eventCacheId({
                id: seg.googleEventId!,
                calendarId: seg.calendarId!,
                title: '',
              }))
          : linkedGroupId
            ? events
                .filter(ev => getEventGroupId(ev) === linkedGroupId)
                .map(ev => eventCacheId(ev))
            : [eventCacheId(editEvent)];
      const updated = removeEventsFromCache(eventsCacheRef.current, cacheKey, keysToDrop);
      if (updated) {
        eventsCacheRef.current = updated;
        setEvents(
          eventsForWindow(
            updated,
            eventsFetchWindow.start.getTime(),
            eventsFetchWindow.end.getTime(),
          ),
        );
      }
    }

    setEditState(null);
    await reloadAll();
  };

  const onPointerDownDrag = (e: React.PointerEvent, ev: DelegateCalendarEventRow) => {
    if (!ev.start || !ev.end) return;
    e.stopPropagation();
    closePeek();
    try {
      const s = parseISO(ev.start);
      const en = parseISO(ev.end);
      dragRef.current = {
        ev,
        pointerId: e.pointerId,
        startMs: s.getTime(),
        endMs: en.getTime(),
        originY: e.clientY,
        moved: false,
      };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onPointerMoveDrag = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    if (Math.abs(e.clientY - d.originY) > 4) d.moved = true;
  };

  const endDrag = async (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId || !execUid) {
      dragRef.current = null;
      return;
    }

    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }

    const dy = e.clientY - d.originY;
    const deltaMin = Math.round((dy / dayBodyPxRef.current) * MINUTES_VISIBLE);

    if (!d.moved || Math.abs(deltaMin) < 2) {
      dragRef.current = null;
      if (!d.moved) {
        openEdit(d.ev, (e.currentTarget as HTMLElement).getBoundingClientRect());
      }
      return;
    }

    dragRef.current = null;

    const deltaMs = deltaMin * 60 * 1000;
    const group = eventIdToGroup.get(d.ev.id);

    try {
      if (group?.id) {
        const planned = shiftScheduledSegments(groupSegmentsToPlanned(group), deltaMs);
        await executiveCalendarSyncEventGroup({
          executiveUid: execUid,
          groupId: group.id,
          timeZone,
          segments: plannedToSyncPayload(planned),
        });
      } else {
        const newStart = new Date(d.startMs + deltaMs);
        const newEnd = new Date(d.endMs + deltaMs);
        await executiveCalendarPatchEvent({
          executiveUid: execUid,
          calendarId: d.ev.calendarId,
          eventId: d.ev.id,
          resource: {
            start: { dateTime: toGoogleCalendarDateTime(newStart), timeZone },
            end: { dateTime: toGoogleCalendarDateTime(newEnd), timeZone },
          },
        });
      }
      await reloadAll();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Could not move event');
    }
  };

  const hourTicks = useMemo(() => {
    const ticks: number[] = [];
    for (let h = DAY_START_HOUR; h <= DAY_END_HOUR; h++) ticks.push(h);
    return ticks;
  }, []);

  const selectedCalendars = useMemo(
    () => calendars.filter(c => selectedCalIds.includes(c.id)),
    [calendars, selectedCalIds],
  );

  return (
    <div className={`ops-hub eac-page${embedded ? ' eac-embedded' : ''}`} ref={pageRef}>
      {!embedded && <AppHeader />}
      <div className="ops-hub-main eac-main">
        {error && (
          <div className="eac-banner err" role="alert">
            {error}
          </div>
        )}

        <div className="ops-hub-toolbar eac-toolbar">
          <div className="ops-hub-toolbar-left">
            <h2 className="ops-hub-title">Executive calendar</h2>
            <div className="ops-hub-stats">
              <span className="ops-stat ops-stat-active">
                {format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}
              </span>
            </div>
          </div>

          <div className="ops-hub-toolbar-right eac-toolbar-right">
            <div className="eac-toolbar-field">
              <span className="ops-modal-label">Week</span>
              <div className="eac-week-range-group">
                <button
                  type="button"
                  className="ops-hide-completed-btn eac-icon-btn"
                  aria-label="Previous week"
                  onClick={() => navigateToMonday(subWeeks(weekStart, 1))}
                >
                  <ChevronLeft size={14} aria-hidden />
                </button>
                <button
                  type="button"
                  className="ops-hide-completed-btn"
                  onClick={() => goToTodayView('smooth')}
                >
                  Today
                </button>
                <button
                  type="button"
                  className="ops-hide-completed-btn eac-icon-btn"
                  aria-label="Next week"
                  onClick={() => navigateToMonday(addWeeks(weekStart, 1))}
                >
                  <ChevronRight size={14} aria-hidden />
                </button>
              </div>
            </div>

            <div className="eac-toolbar-field">
              <span className="ops-modal-label">Zoom</span>
              <div className="eac-week-range-group eac-zoom-range-group">
                <button
                  type="button"
                  className="ops-hide-completed-btn eac-icon-btn"
                  aria-label="Zoom out"
                  disabled={zoom <= ZOOM_MIN}
                  onClick={() => nudgeZoom(-1)}
                >
                  <Minus size={14} aria-hidden />
                </button>
                <button
                  type="button"
                  className="ops-hide-completed-btn eac-zoom-label"
                  onClick={() => setZoom(1)}
                  title="Reset zoom"
                >
                  {Math.round(zoom * 100)}%
                </button>
                <button
                  type="button"
                  className="ops-hide-completed-btn eac-icon-btn"
                  aria-label="Zoom in"
                  disabled={zoom >= ZOOM_MAX}
                  onClick={() => nudgeZoom(1)}
                >
                  <Plus size={14} aria-hidden />
                </button>
              </div>
            </div>

            {calendars.length > 0 && (
              <div className="eac-toolbar-field">
                <span className="ops-modal-label">Calendars</span>
                <div className="eac-cal-hover-zone">
                  <button type="button" className="ops-hide-completed-btn eac-toolbar-btn eac-cal-trigger">
                    <Layers size={13} aria-hidden />
                    {selectedCalIds.length > 0 ? (
                      <span className="eac-cal-trigger-count">{selectedCalIds.length}</span>
                    ) : (
                      'Select'
                    )}
                  </button>
                <div className="eac-cal-flyout" role="group" aria-label="Calendar visibility">
                  <div className="eac-cal-flyout-head">
                    <span className="ops-modal-label">Show calendars</span>
                    <span className="eac-cal-flyout-meta">
                      {selectedCalIds.length} of {calendars.length} selected
                    </span>
                  </div>
                  <div className="eac-cal-selected-preview">
                    {selectedCalendars.map(cal => (
                      <span
                        key={cal.id}
                        className="eac-cal-chip"
                        title={cal.summary || cal.id}
                      >
                        <span
                          className="eac-cal-chip-swatch"
                          style={{ background: cal.backgroundColor || '#94a3b8' }}
                        />
                        {cal.summary || cal.id}
                      </span>
                    ))}
                    {selectedCalendars.length === 0 && (
                      <span className="eac-cal-flyout-empty">No calendars selected</span>
                    )}
                  </div>
                  <div className="eac-cal-flyout-list">
                    {calendars.map(cal => (
                      <label key={cal.id} className="eac-cal-toggle">
                        <input
                          type="checkbox"
                          checked={selectedCalIds.includes(cal.id)}
                          onChange={ev => toggleCal(cal.id, ev.target.checked)}
                        />
                        <span
                          className="eac-cal-swatch"
                          style={{ background: cal.backgroundColor || '#94a3b8' }}
                        />
                        <span className="eac-cal-toggle-label">{cal.summary || cal.id}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              </div>
            )}

            {execUid && (
              <div className="eac-toolbar-field">
                <span className="ops-modal-label eac-toolbar-label-spacer" aria-hidden="true">
                  Actions
                </span>
                <div className="eac-toolbar-btn-row">
                  <button
                    type="button"
                    className="ops-new-btn eac-toolbar-btn eac-toolbar-btn-primary"
                    onClick={() => setTravelChatOpen(open => !open)}
                  >
                    <Calendar size={13} aria-hidden />
                    Calendar Agent
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="ops-hub-content eac-content">
          <p className="eac-zoom-hint">Pinch or Ctrl + scroll to zoom the timeline</p>
          {showInitialLoader ? (
            <div className="ops-hub-loading">
              <div className="ops-hub-loading-bar" aria-label="Loading calendar" />
            </div>
          ) : showCalendarGrid ? (
          <div className="eac-week-shell">
            {loadingData && (
              <div className="eac-events-loading-bar" aria-label="Loading events" />
            )}
            <div className="eac-week-scroller" ref={bindHScrollRef}>
              {weekStrip.map((panelMonday, weekIdx) => {
                const mountPanel = Math.abs(weekIdx - visibleWeekIdx) <= 1;
                if (!mountPanel) {
                  return (
                    <EacWeekSlot
                      key={panelMonday.toISOString()}
                      panelMonday={panelMonday}
                      weekIdx={weekIdx}
                    />
                  );
                }
                return (
                  <EacWeekPanel
                    key={panelMonday.toISOString()}
                    panelMonday={panelMonday}
                    weekIdx={weekIdx}
                    panelDays={daysInWeek(panelMonday)}
                    eventsByDate={eventsByDate}
                    calendarColors={calendarColors}
                    eventIdToGroup={eventIdToGroup}
                    dayBodyPx={dayBodyPx}
                    hourCellPx={hourCellPx}
                    nowTopPct={nowTopPct}
                    calendarNow={calendarNow}
                    hourTicks={hourTicks}
                    dayStartHour={DAY_START_HOUR}
                    dayEndHour={DAY_END_HOUR}
                    isActive={Math.abs(weekIdx - visibleWeekIdx) <= 1}
                    registerVScrollEl={registerVScrollEl}
                    onVerticalScroll={handleVerticalScroll}
                    onDayBodyClick={handleDayBodyClick}
                    onPointerDownDrag={onPointerDownDrag}
                    onPointerMoveDrag={onPointerMoveDrag}
                    endDrag={endDrag}
                    onEventHover={handleEventHover}
                    onEventHoverEnd={handleEventHoverEnd}
                  />
                );
              })}
            </div>
          </div>
          ) : (
            <div className="ops-hub-loading">
              <div className="ops-hub-loading-bar" aria-label="Loading calendar" />
            </div>
          )}
        </div>

        {composerOpen && composerDay && (
          <div
            className="eac-modal-overlay"
            role="dialog"
            aria-modal
            onMouseDown={e => {
              if (e.target === e.currentTarget) setComposerOpen(false);
            }}
          >
            <div className="eac-modal">
              <h3>New event</h3>
              <div className="eac-templates">
                {TEMPLATES.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    className="eac-template-btn"
                    onClick={() => applyTemplate(t.id)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="eac-field">
                <label>Title</label>
                <input
                  value={composerTitle}
                  onChange={e => setComposerTitle(e.target.value)}
                  placeholder="Event title"
                />
              </div>
              <div className="eac-field">
                <label>Calendar</label>
                <select value={composerCalId} onChange={e => setComposerCalId(e.target.value)}>
                  {writableCalendars.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.summary || c.id}
                    </option>
                  ))}
                </select>
              </div>
              <div className="eac-field" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div>
                  <label>Start (time)</label>
                  <input
                    type="time"
                    value={`${String(Math.floor(composerStartMin / 60)).padStart(2, '0')}:${String(composerStartMin % 60).padStart(2, '0')}`}
                    onChange={e => {
                      const [hh, mm] = e.target.value.split(':').map(Number);
                      setComposerStartMin(hh * 60 + mm);
                    }}
                  />
                </div>
                <div>
                  <label>Duration (min)</label>
                  <input
                    type="number"
                    min={5}
                    step={5}
                    value={composerDuration}
                    onChange={e => setComposerDuration(Number(e.target.value))}
                  />
                </div>
              </div>
              <div className="eac-field">
                <label>Location</label>
                <input
                  value={composerLocation}
                  onChange={e => setComposerLocation(e.target.value)}
                  placeholder="Optional"
                />
              </div>
              <div className="eac-field">
                <label>Notes</label>
                <textarea value={composerDesc} onChange={e => setComposerDesc(e.target.value)} placeholder="Optional" />
              </div>
              <div className="eac-modal-actions">
                <button type="button" onClick={() => setComposerOpen(false)}>
                  Cancel
                </button>
                <button type="button" className="primary" onClick={() => void submitComposer()}>
                  Create
                </button>
              </div>
            </div>
          </div>
        )}

        {peek && !editState && (
          <EacClusterPeek
            cluster={peek.cluster}
            anchorRect={peek.anchor}
            calendarColors={calendarColors}
            onSelectEvent={(ev, rowRect) => openEdit(ev, rowRect)}
            onClose={closePeek}
            onPointerEnterCard={cancelPeekClose}
          />
        )}

        {editState && (
          <EventEditorPopover
            key={`${editState.ev.calendarId}-${editState.ev.id}`}
            event={editState.ev}
            anchorRect={editState.anchor}
            calendarName={
              calendars.find(c => c.id === editState.ev.calendarId)?.summary ||
              editState.ev.calendarId
            }
            calendarColor={calendarColors.get(editState.ev.calendarId) || '#bfdbfe'}
            isGrouped={isGroupedEvent(editState.ev)}
            onSave={saveEdit}
            onDelete={deleteEdit}
            onClose={() => setEditState(null)}
          />
        )}

        {slotMenu && (
          <div
            className="eac-slot-menu-overlay"
            onClick={() => setSlotMenu(null)}
            onKeyDown={() => undefined}
            role="presentation"
          >
            <div
              className="eac-slot-menu"
              style={{ top: slotMenu.y, left: slotMenu.x }}
              role="menu"
            >
              <button
                type="button"
                onClick={() => openComposerAt(slotMenu.day, slotMenu.minute)}
              >
                Single event
              </button>
              <button
                type="button"
                onClick={() => openBuilderAt(slotMenu.day, slotMenu.minute)}
              >
                Build trip
              </button>
            </div>
          </div>
        )}

        {travelChatOpen && (
          <Suspense fallback={null}>
            <TravelAgentChatPanel
              onClose={() => setTravelChatOpen(false)}
              executiveUid={execUid ?? undefined}
              timeZone={timeZone}
              onCalendarRefresh={() => void reloadAll()}
            />
          </Suspense>
        )}

        {builderOpen && execUid && (
          <EventBuilderModal
            executiveUid={execUid}
            settings={calSettings}
            calendars={calendars}
            writableCalendarIds={writableCalendarIds}
            existingEvents={events}
            eventGroups={eventGroups}
            timeZone={timeZone}
            initialDay={builderDay ?? startOfDay(new Date())}
            initialStartMin={builderStartMin}
            onClose={() => {
              setBuilderOpen(false);
              setBuilderDay(null);
            }}
            onCreated={() => void reloadAll()}
          />
        )}
      </div>
    </div>
  );
};

export default ExecutiveAssistantCalendarPage;
